<script setup lang="ts">
/**
 * The account, at the navbar's right end, as Tencent Cloud's is: the name
 * over the account ID beside the avatar, and behind them a panel — who is
 * signed in (VIP and 等级 beside the account's name) and how far verified,
 * the balance with 充值, what waits on
 * the customer, the account's own pages, 退出登录. The numbers are read
 * each time the panel opens.
 */
import { computed, ref, watch } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { IconLock, IconGift, IconHistory, IconCode, IconExport, IconUser, IconUserGroup } from '@arco-design/web-vue/es/icon'
import { api, type BillingOverview } from '@/api/client'
import { useAuthStore } from '@/store/auth'
import { ticketSummary, refreshTickets } from '@/store/tickets'
import { attentionItems, type DashSummary } from '@/utils/dashboard'
import { stamp } from '@/utils/format'
import { navPanel } from '@/utils/layout'
import { isNarrow } from '@/utils/responsive'
import { monthStart } from '@/utils/wallet'
import RealnameMark from '@/components/RealnameMark.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import VipBadge from '@/components/VipBadge.vue'
import LvBadge from '@/components/LvBadge.vue'
import { branding } from '@/utils/branding'
import NavWallet from './NavWallet.vue'

const { t } = useI18n()
const router = useRouter()
const auth = useAuthStore()

// One panel out at a time (navPanel): 费用's or 工单's goes when this one
// comes.
const open = computed(() => navPanel.value === 'account')
function onVisible(v: boolean) {
  if (v) navPanel.value = 'account'
  else if (open.value) navPanel.value = null
}
const bill = ref<BillingOverview | null>(null)
const dash = ref<DashSummary | null>(null)
const readAt = ref('')

const user = computed(() => auth.user)
// A 子用户 signed in: its own name, and whose account it is in.
const sub = computed(() => user.value?.sub_user)
// The nickname 账户资料 gave the account, else its username — or the
// sub-user's name.
const shownName = computed(() => sub.value?.username || user.value?.nickname || user.value?.username || '')

async function load() {
  const [b, d] = await Promise.allSettled([
    api.billingOverview({ since: monthStart() }),
    api.dashboard({ tz_offset: -new Date().getTimezoneOffset() }),
    refreshTickets(),
  ])
  if (b.status === 'fulfilled') bill.value = b.value
  if (d.status === 'fulfilled') dash.value = d.value
  readAt.value = stamp(new Date().toISOString()).slice(0, 11)
}
watch(open, (v) => {
  if (v) void load()
})

// What waits on the customer, each to the page that settles it.
const stats = computed(() => [
  { key: 'unpaid', n: bill.value?.pending_orders ?? 0, route: 'billing' },
  { key: 'toRenew', n: dash.value?.subscriptions.expiring ?? 0, route: 'my-packages' },
  { key: 'ticketsTodo', n: ticketSummary.value?.replied ?? 0, route: 'tickets' },
  { key: 'unread', n: attentionItems(dash.value).length, route: 'dashboard' },
])

const links = computed(() => [
  { route: 'account-center', label: 'accountCenter.title', icon: IconUser },
  { route: 'account-security', label: 'securityCenter.title', icon: IconLock },
  ...(sub.value ? [] : [{ route: 'sub-users', label: 'subUsers.title', icon: IconUserGroup }]),
  { route: 'my-packages', label: 'myPackages.title', icon: IconGift },
  { route: 'finance-ledger', label: 'ledger.title', icon: IconHistory },
  { route: 'credentials', label: 'credentials.title', icon: IconCode },
])

function go(name: string) {
  to({ name })
}
function to(where: RouteLocationRaw) {
  navPanel.value = null
  router.push(where)
}
function logout() {
  navPanel.value = null
  auth.logout()
  // The guard sends a signed-out page to 登录 only on the next navigation.
  void router.replace({ name: 'login' })
}
</script>

<template>
  <!-- Opens under the pointer, as Tencent Cloud's does; a phone has no
       pointer, so a tap. The panel's top is the bar's rule: the cell is the
       bar's full height, 1px above it. -->
  <a-trigger
    :popup-visible="open"
    :trigger="isNarrow ? 'click' : 'hover'"
    position="br"
    :popup-offset="1"
    :mouse-leave-delay="200"
    @popup-visible-change="onVisible"
  >
    <button type="button" class="account" :class="{ bare: isNarrow, open }">
      <span v-if="!isNarrow" class="account-text">
        <span class="account-name">{{ shownName }}</span>
        <span class="account-sub">{{ user ? t(sub ? 'navbar.subAccountId' : 'navbar.accountId', { id: user.id }) : '' }}</span>
      </span>
      <UserAvatar v-if="sub" :size="32" :name="sub.username" />
      <UserAvatar v-else :size="32" :name="user?.username" :src="user?.avatar_url" />
    </button>
    <template #content>
      <div class="nav-panel at-edge">
        <div class="head">
          <div class="who">
            <span class="who-name">{{ shownName }}</span>
            <a-tag v-if="sub" color="arcoblue" size="small" class="who-tag">{{ t('navbar.subUser') }}</a-tag>
            <template v-else-if="user">
              <VipBadge v-if="user.vip_level" class="badge" :level="user.vip_level" :variant="branding()?.level_badge" @click="go('account-member')" />
              <LvBadge v-if="user.exp_level" class="badge" :lv="user.exp_level.lv" @click="go('account-member')" />
            </template>
          </div>
          <div class="who-id">
            {{ user ? (sub ? t('navbar.subOwner', { name: user.username, id: user.id }) : t('navbar.accountIdFull', { id: user.id })) : '' }}
            <RealnameMark @go="to" />
          </div>
          <NavWallet
            class="head-wallet"
            :balance-cents="bill?.balance_cents"
            :spent-cents="bill ? (bill.spent_since_cents ?? 0) : undefined"
            :read-at="readAt"
            @top-up="go('billing')"
          />
        </div>
        <div class="stats">
          <button v-for="s in stats" :key="s.key" type="button" class="stat" @click="go(s.route)">
            <span class="stat-n">{{ s.n }}</span>
            <span class="stat-label">{{ t(`navbar.${s.key}`) }}</span>
          </button>
        </div>
        <div class="nav-panel-group">
          <button v-for="l in links" :key="l.route" type="button" class="nav-panel-row" @click="go(l.route)">
            <component :is="l.icon" />{{ t(l.label) }}
          </button>
        </div>
        <div class="nav-panel-group">
          <button type="button" class="nav-panel-row" @click="logout">
            <IconExport />{{ t('app.logout') }}
          </button>
        </div>
      </div>
    </template>
  </a-trigger>
</template>

<style scoped>
/* The trigger: its name over its ID, right-aligned against the avatar —
   a cell the bar's full height, out to the window's edge (App.vue's
   .account-item), Arco's hover grey over all of it while the panel is out.
   A phone's is the avatar alone. */
.account {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 100%;
  padding: 0 var(--nav-edge, 20px) 0 16px;
  border: none;
  border-radius: 0;
  background: none;
  font: inherit;
  text-align: right;
  cursor: pointer;
  transition: background-color 0.1s;
}
.account:hover,
.account.open {
  background-color: var(--color-fill-2);
}
.account.bare {
  padding-left: 8px;
}
.account-text {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: 140px;
}
.account-name,
.account-sub {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.account-name {
  color: var(--color-text-1);
  font-size: 14px;
  line-height: 20px;
}
.account-sub {
  color: var(--color-text-2);
  font-size: 12px;
  line-height: 18px;
}

/* The panel is .nav-panel (base.css), flush with the window's edge; the
   head here is who is signed in, then what waits on them. */
.head {
  padding: 20px 20px 0;
}
.who {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.who-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-text-1);
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
}
.badge {
  cursor: pointer;
}
.who-id {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  margin-top: 6px;
  color: var(--color-text-2);
  font-size: 13px;
  line-height: 20px;
}
.head-wallet {
  margin-top: 16px;
}
.stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding: 12px 8px;
}
.stat {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 8px 12px;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.1s;
}
.stat:hover {
  background-color: var(--color-fill-2);
}
.stat-n {
  color: var(--color-text-1);
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
  font-variant-numeric: tabular-nums;
}
.stat-label {
  color: var(--color-text-3);
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}
</style>
